import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { FeaturesService } from 'src/app/proxy/skybound/sky-gen1/features/features.service';
import { PharmacyFacadeService } from './services/pharmacy-facade.service';
import { AuthFacadeService } from './services/auth-facade.service';
import { PharmacyProfile } from './models/pharmacy-profile.model';
import { environment } from 'src/environments/environment';
import { AppConstants } from './app.constants';
import { OAuthService } from 'angular-oauth2-oidc';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { MessageService } from './services/message.service';
import { NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
	standalone: false,
})
export class AppComponent implements OnInit
{
	public profile = signal<PharmacyProfile | null>(null);
	public loggedIn = signal<boolean>(false);
	public currentYear: number = new Date().getFullYear();
	public menuDisabled: boolean = false;
	public brandName: string = environment.application.name;
	public pharmacyPrefix: string = AppConstants.pharmacyPrefix;
	public notifications = inject(MessageService).notificationCounts;
	public repeats = inject(MessageService).repeatCount;
	public isNative: boolean = Capacitor.getPlatform() !== 'web';
	public isDesktop: boolean = Capacitor.getPlatform() === 'desktop';
	public shopEnabled: boolean = false;
	
	constructor(
		private router: Router,
		private ngZone: NgZone,
		public pharmacyFacadeService: PharmacyFacadeService,
		public authFacadeService: AuthFacadeService,
		private oAuthService: OAuthService, // Inject OAuthService
		private messageService: MessageService,
		private featuresService: FeaturesService
	)
	{
	    this.initializeApp();
	}

	private async initializeApp()
	{
		// Block unexpected OAuth redirects (e.g. triggered by angular-oauth2-oidc on native)
		(this.oAuthService as any).config.openUri = (uri: string) => {
			if (uri.includes('/connect/authorize')) {
				console.warn('Blocked unexpected auth redirect to:', uri);
				return;
			}
			window.location.href = uri;
		};

		// Handle deep links for native platforms
		if (this.isNative)
		{
			App.addListener('appUrlOpen', (event: URLOpenListenerEvent) =>
			{
				this.ngZone.run(() =>
				{
					const url = new URL(event.url);

					const slug = event.url.split(/\.app|\.nz/).pop();
					if (slug) {
						this.router.navigateByUrl(slug);
					}
				});
			});
		}

		effect(() =>
		{
			this.loggedIn.set(this.authFacadeService.isLoggedIn());
		});

		// Setup automatic silent refresh for tokens
		this.oAuthService.setupAutomaticSilentRefresh();
		
		const refreshToken = this.oAuthService.getRefreshToken();
		const expires  = this.oAuthService.getAccessTokenExpiration();
		// console.debug('AppComponent constructor - refreshToken:', refreshToken, 'expires:', expires);
		if (refreshToken && expires)
		{
			this.authFacadeService.saveBiometricCredentialsWithTokenAndExpiry(expires, refreshToken);
		}

		// Check if shop is enabled
		const features = await firstValueFrom(this.featuresService.get());
		this.shopEnabled = features?.shopEnabled ?? false;
	}

	ngOnInit()
	{
		//set the document.title
		document.title = environment.application.name;
		this.pharmacyFacadeService.currentPharmacyProfile$.subscribe(profile =>
		{
			this.profile.set(profile);
		});
	}

	public vaccinationBookingUrl: string = AppConstants.vaccinationBookingUrl;
	public bookVaccination(event:any)
	{
		event.preventDefault();
		Browser.open({ url: this.vaccinationBookingUrl });
	}

	public privacyPolicyUrl: string = environment['privacyPolicyUrl'] || 'https://healthmax.nz/terms';
	public termsUrl: string = environment['termsUrl'] || 'https://healthmax.nz/terms';

	public loadHref(event: any): void
	{
		event.preventDefault();
		Browser.open({ url: event.target.href });
	}
}
